"use client";

import { type ReactNode, useCallback, useRef, useState } from "react";
import "./RolloutSchedule.css";

/* -------------------------------------------------------------------------- */
/* Types                                                                        */
/* -------------------------------------------------------------------------- */

/** Visual status of a rollout month cell. */
export type MonthStatus = "offline" | "ramp" | "steady";

export interface RolloutScheduleProps {
  /**
   * Exactly 12 values in the range [0, 1] representing the capture percentage
   * per month (0 = offline, 1 = 100% steady-state capture).
   */
  schedule: number[];
  /**
   * Optional per-cell labels. When omitted, cells are labelled sequentially
   * (`M1`, `M2`, …). Supply this to render a milestone-only view where each
   * cell maps to a real, non-sequential month (e.g. `M1`, `M3`, `M6`).
   */
  labels?: string[];
  /** Optional per-cell metadata for compressed ranges in read-only displays. */
  collapsedRanges?: Array<
    | {
        startMonth: number;
        endMonth: number;
        hiddenCount: number;
      }
    | undefined
  >;
  /** Called when a collapsed range cell is expanded. */
  onExpandCollapsedRange?: (range: {
    startMonth: number;
    endMonth: number;
    hiddenCount: number;
  }) => void;
  /** Called when the user commits a new percentage for a month. */
  onChange: (monthIndex: number, pct: number) => void;
  /** Optional slot rendered on the right side of the header (e.g. ApprovalCircle). */
  headerSlot?: ReactNode;
  /** When true, percentages render as static values instead of inline editors. */
  readOnly?: boolean;
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                      */
/* -------------------------------------------------------------------------- */

function monthStatus(pct: number): MonthStatus {
  if (pct <= 0) return "offline";
  if (pct >= 1) return "steady";
  return "ramp";
}

const STATUS_LABEL: Record<MonthStatus, string> = {
  offline: "Offline",
  ramp: "Ramp",
  steady: "Steady",
};

/* -------------------------------------------------------------------------- */
/* Component                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * 12-month rollout schedule grid with liquid-tank fill indicators and
 * inline editing.  Fully controlled — schedule and onChange supplied by the
 * parent.
 */
export function RolloutSchedule({
  schedule,
  labels,
  collapsedRanges,
  onExpandCollapsedRange,
  onChange,
  headerSlot,
  readOnly = false,
}: RolloutScheduleProps) {
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const cancelledRef = useRef(false);
  const focusRef = useCallback(
    (el: HTMLInputElement | null) => el?.focus(),
    [],
  );

  return (
    <section className="rl-section">
      <header className="rl-head">
        <div>
          <h2>Rollout schedule</h2>
          <p className="rl-sub">
            Expected capture ramp from kickoff to steady state. This drives
            every monthly benefit in the investment curve.
          </p>
        </div>
        {headerSlot}
      </header>

      <div
        className={["rl-grid", schedule.length <= 8 ? "rl-grid-fill" : ""]
          .filter(Boolean)
          .join(" ")}
      >
        {schedule.map((pct, i) => {
          const collapsedRange = collapsedRanges?.[i];
          if (collapsedRange) {
            // The hidden months hold steady at a single carried-over rate, so
            // surface it as a (dimmed, dashed) bar aligned to the same baseline
            // as real month cells — letting the eye compare capture straight
            // across the row instead of jumping over a blank gap.
            const collapsedPct = Math.round(pct * 100);
            return (
              <div key={i} className="rl-cell rl-cell-collapsed">
                <button
                  type="button"
                  className="rl-collapse-button"
                  aria-label={`Show months ${collapsedRange.startMonth} through ${collapsedRange.endMonth}`}
                  onClick={() => onExpandCollapsedRange?.(collapsedRange)}
                >
                  <span className="rl-cell-label">
                    {labels?.[i] ??
                      `M${collapsedRange.startMonth}-M${collapsedRange.endMonth}`}
                  </span>
                  <span
                    className="rl-tank rl-tank-collapsed"
                    role="img"
                    aria-label={`${collapsedPct}% capture`}
                  >
                    <span
                      className="rl-tank-fill"
                      style={{ height: `${collapsedPct}%` }}
                    />
                  </span>
                  <span className="rl-pct rl-pct-static">{collapsedPct}%</span>
                  <span className="rl-collapse-count">
                    +{collapsedRange.hiddenCount} months
                  </span>
                </button>
              </div>
            );
          }

          const status = monthStatus(pct);
          const displayPct = Math.round(pct * 100);
          return (
            <div key={i} className={`rl-cell ${status}`}>
              <div className="rl-cell-label">{labels?.[i] ?? `M${i + 1}`}</div>

              <div
                className="rl-tank"
                role="img"
                aria-label={`${displayPct}% capture`}
              >
                <div
                  className="rl-tank-fill"
                  style={{ height: `${displayPct}%` }}
                />
              </div>

              <div className="rl-value">
                {editingIdx === i ? (
                  <input
                    type="number"
                    min={0}
                    max={100}
                    defaultValue={displayPct}
                    ref={focusRef}
                    onBlur={(e) => {
                      if (!cancelledRef.current) {
                        const v = Number(e.currentTarget.value);
                        if (!Number.isNaN(v))
                          onChange(i, Math.min(100, Math.max(0, v)) / 100);
                      }
                      cancelledRef.current = false;
                      setEditingIdx(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") e.currentTarget.blur();
                      if (e.key === "Escape") {
                        cancelledRef.current = true;
                        setEditingIdx(null);
                      }
                    }}
                    className="rl-input"
                  />
                ) : readOnly ? (
                  <span className="rl-pct rl-pct-static">{displayPct}%</span>
                ) : (
                  <button
                    type="button"
                    className="rl-pct"
                    onClick={() => setEditingIdx(i)}
                  >
                    {displayPct}%
                  </button>
                )}
              </div>

              <div className={`rl-status ${status}`}>
                {STATUS_LABEL[status]}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
